import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Language = "de" | "en";

const translations = {
  de: {
    nav: { events: "Events", about: "Über AXON", family: "Family", contact: "Kontakt", next: "Nächstes Event" },
    hero: {
      eyebrow: "Düsseldorf · House & Afro House",
      rhythm: "Rhythmus",
      connects: "verbindet",
      people: "Menschen.",
      intro: "AXON macht aus Sound Verbindung — Open-Air-Energie, Bewegung bis spät in die Nacht und eine Community, die jeden Beat fühlt.",
      explore: "Events entdecken",
      join: "Family\nbeitreten",
      script: "spür die Verbindung",
    },
    ticker: "HOUSE × AFRO HOUSE × DÜSSELDORF × EINE FREQUENZ ×",
    events: {
      eyebrow: "Komm, wie du bist",
      title: "Upcoming\nEvents",
      intro: "Ein Raum. Ein Rhythmus. Alle verbunden. Entdecke den nächsten AXON-Moment und registriere dich.",
      nextUp: "Als Nächstes",
      with: "Mit",
      register: "Registrieren",
      details: "Eventdetails ansehen",
      flyerAlt: "Flyer für",
    },
    eventPage: {
      back: "Alle Events",
      overview: "Eventübersicht",
      dateTime: "Datum & Uhrzeit",
      location: "Location",
      lineup: "Line-up",
      admission: "Eintritt",
      registeredPrice: "Mit bestätigter Registrierung",
      doorPrice: "Ohne Registrierung",
      payment: "Bezahlung",
      paymentDetail: "Der Eintritt wird ausschließlich bar vor Ort bezahlt.",
      confirmation: "Bestätigung",
      confirmationDetail: "Nach deiner E-Mail-Bestätigung erhältst du Ticket und Reservierungsbestätigung automatisch per E-Mail.",
      drinks: "Getränke",
      drinksDetail: "Getränke sind nicht inklusive und können an der Bar gekauft werden.",
      minimumSpend: "Kein Mindestverzehr",
      registrationNote: "Die Registrierung selbst ist kostenlos. Nach deiner E-Mail-Bestätigung erhältst du dein Ticket automatisch per E-Mail.",
      share: "Event teilen",
      copied: "Link kopiert",
    },
    about: {
      eyebrow: "Geboren in Düsseldorf",
      title: "Keine Gästeliste.\nEine gemeinsame Frequenz.",
      body: "AXON bringt House und Afro House an Orte, an denen Rhythmus persönlich wird. Keine Distanz hinter der Samtkordel — nur Bewegung, Wärme und Menschen, die aus demselben Grund gekommen sind.",
      follow: "@axon.vibes folgen",
    },
    family: {
      eyebrow: "Zuerst erfahren. Zuerst bewegen.",
      title: "Join the\nfamily.",
      intro: "Erhalte Event-Drops, geheime Locations, Line-ups und Early Access direkt von AXON.",
    },
    contact: {
      eyebrow: "Bookings · Partner · Ideen",
      title: "Lass uns etwas\nbewegen.",
    },
    footer: {
      identity: "House & Afro House · Düsseldorf",
      imprint: "Impressum",
      privacy: "Datenschutz",
    },
    form: {
      name: "Name",
      namePlaceholder: "Dein Name",
      email: "E-Mail",
      city: "Stadt",
      cityPlaceholder: "Düsseldorf",
      sound: "Dein Sound",
      choose: "Bitte wählen",
      house: "House",
      afro: "Afro House",
      melodicTechno: "Melodic Techno",
      all: "Alle",
      message: "Nachricht",
      messagePlaceholder: "Booking, Kooperation oder einfach Hallo sagen …",
      optIn: "Ich möchte Neuigkeiten zu kommenden AXON-Events erhalten. Die Einwilligung kann ich jederzeit widerrufen.",
      send: "Nachricht senden",
      join: "Family beitreten",
      sending: "Wird gesendet …",
      contactSuccess: "Nachricht angekommen. Wir melden uns.",
      familySuccess: "Du bist dabei. Willkommen in der Family.",
      genericError: "Etwas ist schiefgelaufen.",
      turnstileRequired: "Bitte bestätige zuerst, dass du kein Bot bist.",
      turnstileError: "Die Sicherheitsprüfung konnte nicht geladen werden. Bitte lade die Seite neu.",
    },
    registration: {
      eyebrow: "Event-Registrierung",
      lead: "Registriere dich für dieses Event. Das ist noch kein Ticketkauf und garantiert keinen Eintritt.",
      phone: "Telefon",
      optional: "Optional",
      instagram: "Instagram-ID",
      instagramPlaceholder: "@deinname (optional)",
      discovery: "Wie hast du uns gefunden?",
      discoveryPlaceholder: "Optional auswählen",
      discoveryInstagram: "Instagram",
      discoveryGoogle: "Google",
      discoveryFriends: "Freunde",
      referrerContact: "E-Mail oder Instagram deines Freundes",
      referrerContactPlaceholder: "Optional",
      referredByLink: "Über einen persönlichen Einladungslink",
      additionalGuests: "Begleitpersonen",
      additionalGuestsHint: "Du kannst bis zu vier Personen mitbringen. Dein Gruppen-Ticket gilt für dich und deine ausgewählten Begleitpersonen.",
      updates: "Halte mich über dieses Event und weitere AXON-Events auf dem Laufenden.",
      submit: "Jetzt registrieren",
      submitting: "Registrierung läuft …",
      liveSuccess: "Fast geschafft: Wir haben eine Bestätigungsmail an {email} geschickt.",
      emailVerificationHint: "Öffne die E-Mail und klicke innerhalb von 48 Stunden auf den Bestätigungslink. Danach erhältst du dein Ticket automatisch per E-Mail. Prüfe bitte auch deinen Spam-Ordner.",
      disclaimer: "Die Registrierung ist kostenlos und unverbindlich. Sie ist keine Eintrittskarte, Reservierung oder Eintrittsgarantie.",
      admissionNotice: "Nach Bestätigung kostet der Eintritt 10 € und wird bar vor Ort bezahlt. Ohne Registrierung beträgt der Eintritt 15 €.",
      shareTitle: "Lade deine Freunde ein",
      shareBody: "Teile deinen persönlichen Eventlink. Freunde landen direkt auf dieser Eventseite und deine Empfehlung wird automatisch zugeordnet.",
      shareCta: "Persönlichen Link teilen",
      shareCopy: "Link kopieren",
      shareCopied: "Link kopiert",
      sharePreparing: "Link wird erstellt …",
      shareError: "Der persönliche Link konnte nicht erstellt werden.",
      shareRetry: "Erneut versuchen",
      shareManual: "Falls dein Browser das Teilen oder Kopieren blockiert, markiere den Link hier manuell:",
      close: "Registrierung schließen",
    },
    cookie: {
      trigger: "Cookie-Einstellungen",
      eyebrow: "Deine Auswahl",
      title: "Cookies, mit Respekt.",
      body: "Notwendige Speicherung merkt sich diese Auswahl. Analyse und Marketing bleiben aus, bis du sie erlaubst.",
      necessary: "Notwendig",
      necessaryDescription: "Erforderlich für die Consent-Einstellungen.",
      analytics: "Analyse",
      analyticsDescription: "Hilft uns, die Nutzung der Seite zu verstehen.",
      marketing: "Marketing",
      marketingDescription: "Ermöglicht die Messung von Kampagnen.",
      necessaryOnly: "Nur notwendig",
      preferences: "Einstellungen",
      save: "Auswahl speichern",
      acceptAll: "Alle akzeptieren",
    },
    metaDescription: "AXON — House- und Afro-House-Events in Düsseldorf. Vibes, Rhythmus und Verbindung.",
  },
  en: {
    nav: { events: "Events", about: "About", family: "Family", contact: "Contact", next: "Next event" },
    hero: {
      eyebrow: "Düsseldorf · House & Afro House",
      rhythm: "Rhythm",
      connects: "connects",
      people: "people.",
      intro: "AXON is where sound becomes connection — open-air energy, late-night movement, and a community that feels every beat.",
      explore: "Explore events",
      join: "Join\nthe family",
      script: "feel the connection",
    },
    ticker: "HOUSE × AFRO HOUSE × DÜSSELDORF × ONE FREQUENCY ×",
    events: {
      eyebrow: "Come as you are",
      title: "Upcoming\nevents",
      intro: "One room. One rhythm. Everyone connected. Discover the next AXON moment and register.",
      nextUp: "Next up",
      with: "With",
      register: "Register",
      details: "View event details",
      flyerAlt: "Flyer for",
    },
    eventPage: {
      back: "All events",
      overview: "Event overview",
      dateTime: "Date & time",
      location: "Location",
      lineup: "Lineup",
      admission: "Admission",
      registeredPrice: "With confirmed registration",
      doorPrice: "Without registration",
      payment: "Payment",
      paymentDetail: "Admission is paid exclusively in cash at the venue.",
      confirmation: "Confirmation",
      confirmationDetail: "After confirming your email, your ticket and reservation confirmation are sent automatically by email.",
      drinks: "Drinks",
      drinksDetail: "Drinks are not included and can be purchased at the bar.",
      minimumSpend: "No minimum spend",
      registrationNote: "Registration itself is free. After confirming your email, your ticket is sent automatically by email.",
      share: "Share event",
      copied: "Link copied",
    },
    about: {
      eyebrow: "Born in Düsseldorf",
      title: "Not a guest list.\nA shared frequency.",
      body: "AXON brings House and Afro House into spaces where rhythm feels personal. No velvet-rope distance — just movement, warmth, and people who came for the same reason.",
      follow: "Follow @axon.vibes",
    },
    family: {
      eyebrow: "First to know. First to move.",
      title: "Join the\nfamily.",
      intro: "Get event drops, secret locations, lineups, and early access straight from AXON.",
    },
    contact: {
      eyebrow: "Bookings · Partners · Ideas",
      title: "Let’s make\nsomething move.",
    },
    footer: {
      identity: "House & Afro House · Düsseldorf",
      imprint: "Imprint",
      privacy: "Privacy",
    },
    form: {
      name: "Name",
      namePlaceholder: "Your name",
      email: "Email",
      city: "City",
      cityPlaceholder: "Düsseldorf",
      sound: "Your sound",
      choose: "Choose one",
      house: "House",
      afro: "Afro House",
      melodicTechno: "Melodic Techno",
      all: "All",
      message: "Message",
      messagePlaceholder: "Bookings, collaborations, or just say hello …",
      optIn: "I want news about upcoming AXON events. I can withdraw my consent at any time.",
      send: "Send message",
      join: "Join the family",
      sending: "Sending …",
      contactSuccess: "Message received. We’ll get back to you.",
      familySuccess: "You’re on the list. Welcome to the family.",
      genericError: "Something went wrong.",
      turnstileRequired: "Please complete the security check first.",
      turnstileError: "The security check could not be loaded. Please reload the page.",
    },
    registration: {
      eyebrow: "Event registration",
      lead: "Register for this event. This is not a ticket purchase and does not guarantee admission.",
      phone: "Phone",
      optional: "Optional",
      instagram: "Instagram ID",
      instagramPlaceholder: "@yourname (optional)",
      discovery: "How did you find us?",
      discoveryPlaceholder: "Choose optionally",
      discoveryInstagram: "Instagram",
      discoveryGoogle: "Google",
      discoveryFriends: "Friends",
      referrerContact: "Your friend’s email or Instagram",
      referrerContactPlaceholder: "Optional",
      referredByLink: "Via a personal invitation link",
      additionalGuests: "Additional guests",
      additionalGuestsHint: "You can bring up to four people. Your group ticket is valid for you and your selected guests.",
      updates: "Keep me in the loop about this event and future AXON events.",
      submit: "Register now",
      submitting: "Registering …",
      liveSuccess: "Almost there: We sent a confirmation email to {email}.",
      emailVerificationHint: "Open the email and click the confirmation link within 48 hours. Your ticket is then sent automatically by email. Please also check your spam folder.",
      disclaimer: "Registration is free and non-binding. It is not a ticket, reservation, or guarantee of admission.",
      admissionNotice: "After confirmation, admission is €10 and paid in cash at the venue. Without registration, admission is €15.",
      shareTitle: "Invite your friends",
      shareBody: "Share your personal event link. Friends land directly on this event page and your referral is attributed automatically.",
      shareCta: "Share personal link",
      shareCopy: "Copy link",
      shareCopied: "Link copied",
      sharePreparing: "Creating link …",
      shareError: "The personal link could not be created.",
      shareRetry: "Try again",
      shareManual: "If your browser blocks sharing or copying, select the link manually here:",
      close: "Close registration",
    },
    cookie: {
      trigger: "Cookie settings",
      eyebrow: "Your choice",
      title: "Cookies, with respect.",
      body: "Necessary storage keeps this choice. Analytics and marketing remain off until you allow them.",
      necessary: "Necessary",
      necessaryDescription: "Required for consent settings.",
      analytics: "Analytics",
      analyticsDescription: "Helps us understand site usage.",
      marketing: "Marketing",
      marketingDescription: "Enables campaign measurement.",
      necessaryOnly: "Necessary only",
      preferences: "Preferences",
      save: "Save selection",
      acceptAll: "Accept all",
    },
    metaDescription: "AXON — House & Afro House events in Düsseldorf. Vibes, rhythm and connection.",
  },
} as const;

type LanguageContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (typeof translations)[Language];
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    const stored = localStorage.getItem("axon_language");
    if (stored === "de" || stored === "en") return stored;
    return "de";
  });

  useEffect(() => {
    localStorage.setItem("axon_language", language);
    document.documentElement.lang = language;
    document.querySelector('meta[name="description"]')?.setAttribute("content", translations[language].metaDescription);
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage, t: translations[language] }), [language]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
}

export function formatEventDate(date: string, language: Language) {
  return new Intl.DateTimeFormat(language === "de" ? "de-DE" : "en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    timeZone: "Europe/Berlin",
  })
    .format(new Date(`${date}T12:00:00+02:00`))
    .replaceAll(".", "")
    .toUpperCase();
}
